import * as React from 'react';
import { FieldRenderProps } from 'react-final-form';

import { FormControlLabel, FormGroup, Grid, makeStyles, Theme } from '@material-ui/core';
import Checkbox, { CheckboxProps } from '@material-ui/core/Checkbox';

interface CheckboxListWrapperProps<T extends any> extends Omit<CheckboxProps, 'onChange'>, FieldRenderProps<T[], any> {
    onChange?: (event: React.ChangeEvent<HTMLInputElement>, value: T[]) => void;
    values: Array<{ value: T; label: string; }>
    valueSelector: (value: T) => any;
}

interface CheckboxInputProps<T extends any> extends Omit<CheckboxProps, 'onChange' | 'value'> {
    onChange: (event: React.ChangeEvent<HTMLInputElement>, value: T, checked: boolean) => void;
    value: T;
    label: string;
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles<Theme>(theme => ({
    error: {
        color: theme.palette.error.main,
        border: '0 !important'
    }
}));

const CheckboxInput = <T extends any>({
    onChange,
    value,
    label,
    ...rest
}: CheckboxInputProps<T>) => {
    const onInputChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>, checkedValue: boolean) => {
        if (onChange) {
            onChange(event, value, checkedValue);
        }
    }, [value, onChange]);

    return (
        <Grid item xs={12}>
            <FormGroup>
                <Grid item xs={10}>
                <FormControlLabel
                    control={
                        <Checkbox
                            {...rest}
                            onChange={onInputChange}
                        />
                    }
                    label={label}
                    />
                </Grid>
            </FormGroup>
        </Grid>
    );
}

export const CheckboxListWrapper = <T extends any>({
    input: { checked, name, onChange: onInputChange, value, onFocus: onInputFocus, onBlur: onInputBlur,  ...restInput },
    meta,
    onChange,
    onFocus,
    onBlur,
    values,
    valueSelector,
    ...rest
}: CheckboxListWrapperProps<T>) => {

    const onChangeCombined = React.useCallback((event: React.ChangeEvent<HTMLInputElement>, checkboxValue: T, checkedValue: boolean) => {
        const newValue = checkedValue ? [...value, checkboxValue] : value?.filter((v: T) => valueSelector ? valueSelector(v) !== valueSelector(checkboxValue) : v !== checkboxValue);

        if (onChange) {
            onChange(event, newValue);
        }

        onInputChange(newValue);
    }, [value, onChange, onInputChange]);

    const classes = useStyles();

    const showError = meta.submitFailed && (meta.error || (meta.submitError && meta.submitError instanceof String && !meta.dirtySinceLastSubmit)) && !meta.submitting;

    return (
        <>
            

            {values.map(v => {
                return (
                    <CheckboxInput
                        key={v.value as any}
                        {...rest}
                        name={name}
                        onChange={onChangeCombined}
                        value={v.value}
                        checked={value?.find(x => valueSelector ? valueSelector(x) === valueSelector(v.value) : x === v.value) != null}
                        label={v.label}
                    />
                );
            })}

            {
                showError && <div className={classes.error}>{meta.error ?? meta.submitError}</div>
            }
        </>
    );
}